import { area, booleanPointInPolygon, buffer, point, pointOnFeature, polygon } from '@turf/turf';
import { ILngLat } from 'domain/types/location/coordinates';
import { Feature, MultiPolygon, Polygon, Position } from 'geojson';
import { err, ok, Result } from 'neverthrow';
import polylabel from 'polylabel';
import { sendError } from 'utils/sendError';

type PolygonCoordinates = Position[][];
type MultiPolygonCoordinates = Position[][][];
type Coordinates = PolygonCoordinates | MultiPolygonCoordinates;

export const isPolygonCoordinates = (
  type: 'polygon' | 'multiPolygon',
  coordinates: Coordinates,
): coordinates is PolygonCoordinates => {
  return type === 'polygon' && !!coordinates;
};

type GetCenterError =
  | 'INVALID_COORDINATES_ERROR'
  | 'CREATE_FEATURE_ERROR'
  | 'POLYLABEL_ERROR'
  | 'POLYGON_BUFFER_ERROR'
  | 'POINT_ON_FEATURE_ERROR'
  | 'GET_IS_BOOLEAN_POINT_IN_POLYGON_ERROR'
  | 'FALLBACK_ERROR'
  | 'VALIDATE_COORDINATE_TYPE_ERROR'
  | 'NORMALIZE_TO_POLYGON_ERROR'
  | 'INVALID_MULTIPOLYGON_ERROR'
  | 'LARGEST_POLYGON_FROM_MULTIPOLYGON_ERROR';

const normalizeToPolygon = (
  coordinates: PolygonCoordinates | MultiPolygonCoordinates,
): Result<PolygonCoordinates, GetCenterError> => {
  return validateCoordinateType(coordinates).andThen((type) => {
    if (isPolygonCoordinates(type, coordinates)) {
      return ok(coordinates);
    } else {
      return getLargestPolygonFromMultiPolygon(coordinates);
    }
  });
};

const validateCoordinateType = (coordinates: unknown): Result<'polygon' | 'multiPolygon', GetCenterError> => {
  try {
    if (!Array.isArray(coordinates) || coordinates.length === 0) {
      return err('INVALID_COORDINATES_ERROR'); // Coordinates must be a non-empty array
    }

    const first = coordinates[0];
    if (!Array.isArray(first) || first.length === 0) {
      return err('INVALID_COORDINATES_ERROR'); // Invalid first level structure
    }

    const second = first[0];
    if (!Array.isArray(second)) {
      return err('INVALID_COORDINATES_ERROR'); // Invalid second level structure
    }

    if (typeof second[0] === 'number' && typeof second[1] === 'number') {
      return ok('polygon');
    } else if (Array.isArray(second[0])) {
      return ok('multiPolygon');
    } else {
      return err('INVALID_COORDINATES_ERROR'); // Unable to determine coordinate type
    }
  } catch (e) {
    return err('VALIDATE_COORDINATE_TYPE_ERROR');
  }
};

export const getLargestPolygonFromMultiPolygon = (
  multiPolygonCoords: MultiPolygonCoordinates,
): Result<PolygonCoordinates, GetCenterError> => {
  try {
    if (!Array.isArray(multiPolygonCoords) || multiPolygonCoords.length === 0) {
      return err('INVALID_MULTIPOLYGON_ERROR'); // Empty or invalid coordinates
    }

    const result = multiPolygonCoords.reduce(
      (acc, coords) => {
        try {
          const feature = polygon(coords);
          const featureArea = area(feature);
          return featureArea > acc.area ? { coords, area: featureArea } : acc;
        } catch (e) {
          // Skip invalid polygons but continue processing others
          return acc;
        }
      },
      { coords: null as PolygonCoordinates | null, area: 0 },
    );

    if (!result.coords) {
      return err('INVALID_MULTIPOLYGON_ERROR'); // No valid polygons found in MultiPolygon
    }

    return ok(result.coords);
  } catch (e) {
    return err('LARGEST_POLYGON_FROM_MULTIPOLYGON_ERROR');
  }
};

const createFeature = (coordinates: Position[][]): Result<Feature<Polygon>, GetCenterError> => {
  try {
    const feature = polygon(coordinates);
    return ok(feature);
  } catch (e) {
    return err('CREATE_FEATURE_ERROR');
  }
};

const createPolylabel = (coordinates: Position[][]): Result<Position, GetCenterError> => {
  try {
    const [lng, lat] = polylabel(coordinates, 1);
    return ok([lng, lat]);
  } catch (e) {
    return err('POLYLABEL_ERROR');
  }
};

const getIsBooleanPointInPolygon = (position: Position, feature: Feature<Polygon>): Result<boolean, GetCenterError> => {
  try {
    return ok(booleanPointInPolygon(point(position), feature));
  } catch (e) {
    return err('GET_IS_BOOLEAN_POINT_IN_POLYGON_ERROR');
  }
};

const getPolygonBuffer = (feature: Feature<Polygon>): Result<Feature<Polygon | MultiPolygon>, GetCenterError> => {
  try {
    const plotPolygonBuffer = buffer(feature, 0.0005, { units: 'kilometers' });
    if (!plotPolygonBuffer) return err('POLYGON_BUFFER_ERROR');

    return ok(plotPolygonBuffer);
  } catch (e) {
    return err('POLYGON_BUFFER_ERROR');
  }
};

const getPointOnFeature = (feature: Feature<Polygon | MultiPolygon>): Result<Position, GetCenterError> => {
  try {
    const pointOnFeatureResult = pointOnFeature(feature);
    return ok(pointOnFeatureResult.geometry.coordinates);
  } catch (e) {
    return err('POINT_ON_FEATURE_ERROR');
  }
};

const getCenterInternal = (
  coordinates: PolygonCoordinates | MultiPolygonCoordinates,
): Result<Position, GetCenterError> => {
  if (!coordinates || coordinates.length === 0) {
    return err('INVALID_COORDINATES_ERROR');
  }

  const normalizedCoordinates = normalizeToPolygon(coordinates);
  if (normalizedCoordinates.isErr()) {
    return err(normalizedCoordinates.error);
  }

  const featureResult = createFeature(normalizedCoordinates.value);
  if (featureResult.isErr()) {
    return err(featureResult.error);
  }

  const polylabelResult = createPolylabel(normalizedCoordinates.value);
  if (polylabelResult.isErr()) {
    return err(polylabelResult.error);
  }

  const isBooleanPointInPolygon = getIsBooleanPointInPolygon(polylabelResult.value, featureResult.value);
  if (isBooleanPointInPolygon.isErr()) {
    return err(isBooleanPointInPolygon.error);
  }

  if (!isBooleanPointInPolygon.value) {
    const polygonBufferResult = getPolygonBuffer(featureResult.value);
    if (polygonBufferResult.isErr()) {
      return err(polygonBufferResult.error);
    }

    const pointOnBufferedFeatureResult = getPointOnFeature(polygonBufferResult.value);
    if (pointOnBufferedFeatureResult.isErr()) {
      return err(pointOnBufferedFeatureResult.error);
    }

    return pointOnBufferedFeatureResult;
  }

  return polylabelResult;
};

export const getCenter = (coordinates: PolygonCoordinates | MultiPolygonCoordinates): ILngLat => {
  const result = getCenterInternal(coordinates);

  return result.match(
    ([lng, lat]) => ({ lng, lat }),
    (error) => {
      sendError(error);
      return { lng: 0, lat: 0 }; // Fallback to default coordinates
    },
  );
};
